import EventData from '../graphData/event/EventData'
import AnalysisActivityCardBody from '../analysis/AnalysisActivityCardBody'
import AnalysisEffectCardBody from '../analysis/AnalysisEffectCardBody'
import AnalysisResearchCardBody from '../analysis/AnalysisResearchCardBody'
import OutputVisualization from '../dataVisualizations/OutputVisualization'
import IndicatorVisualization from '../dataVisualizations/IndicatorVisualization'
import DashboardCardOptions from './DashboardCardOptions'
import DashboardEffectTargetgroupFilter from './DashboardEffectTargetgroupFilter'
import CardPeriodControl from './CardPeriodControl'
import CardHeaderSlotContext from '../../StateManagment/CardHeaderSlot'
import ComparisonFrame from '../dataVisualizations/ComparisonFrame'
import IndicatorCompareView from '../dataVisualizations/IndicatorCompareView'
import { useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'
import {
    useFirestoreGeneral,
    useFirestoreGeneralOrderBy,
    useFirestoreNoCompagny,
} from '../../firebase/useFirestore'
import useEffectTargetgroups from '../../hooks/useEffectTargetgroups'
import {
    getDefaultGraphType,
} from '../../helpers/visualisationDefaults'
import { DASHBOARD_FILTER_FIELDS } from '../../helpers/dashboardHelpers/dashboardCardFilters'
import resolveComparePeriods from '../../helpers/compareHelpers/resolveComparePeriods'
import periodRangeToDates from '../../helpers/liveReportHelpers/periodRangeToDates'
import { EmptyStateProvider } from '../../StateManagment/EmptyStateContext'
import Modal from '../common/Modal'
import Tooltip from '../common/Tooltip'
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined'

// Stable empty array — returned from the override memo when the lookup misses
// so reference equality holds across renders.
const EMPTY_RESEARCH_IDS = []

// Maps dashboard item type → user-facing Dutch label
const TYPE_LABELS = {
    indicator: 'Vraag',
    output: 'Output',
    outputIndicator: 'Output',
    activity: 'Activiteit',
    effect: 'Effect',
    research: 'Onderzoek',
}

// Standalone pill component — used both in CardLabel (child cards) and
// as a headerAction slot inside parent Analysis card bodies.
// When isMsi is true, displays "MSI-effect" with a dedicated MSI pill style.
const TypeLabelPill = ({ type, isMsi = false }) => {
    const variant = isMsi ? 'msi' : type
    const text = isMsi ? 'MSI-effect' : (TYPE_LABELS[type] || type)
    return (
        <span className={`new-dashboard-card-label-type new-dashboard-card-label-type-${variant}`}>
            {text}
        </span>
    )
}

// Renders the label row above child-type dashboard cards.
// Loads the parent effect once to use for both the context name (indicator cards)
// and the MSI check (Standard === "true" means the effect comes from the Meetstandaard).
const CardLabel = ({ item }) => {
    const { Type, Label, EffectId } = item

    const effects = useFirestoreNoCompagny('OutputEffects', 'ID', EffectId || '')
    const effect = effects && effects.length > 0 ? effects[0] : null
    const isMsi = effect?.Standard === 'true'

    const contextName = Type === 'indicator'
        ? (effect?.Effect || Label)
        : (Label ? Label.split(' — ')[0] : Label)

    return (
        <p className='new-dashboard-card-label'>
            <span>{contextName}</span>
            <TypeLabelPill type={Type} isMsi={isMsi && Type === 'indicator'} />
        </p>
    )
}

// Output visualisation — needs EventForms + EventFields to get the field doc.
// Current 'output' type stores an eventField (form field). No sub-items, so
// the DashboardCardOptions color icon will auto-hide.
const OutputCardContent = ({ item, graphType, startDate, endDate, hideControls }) => {
    const eventForms = useFirestoreGeneral('EventForms', 'ActivityID', item.ActivityId || '')
    const eventFormId = Array.isArray(eventForms) && eventForms.length > 0 ? eventForms[0].ID : ''
    // Scoped fetch — only EventFields belonging to this activity's EventForm
    const eventFields = useFirestoreGeneralOrderBy('EventFields', 'EventId', eventFormId, 'Position', 'asc')
    const field = Array.isArray(eventFields)
        ? eventFields.find(f => f.ID === item.IndicatorId)
        : null

    if (!field) return <p className='new-dashboard-card-empty'>Geen data gevonden</p>

    const resolvedGraphType = graphType || getDefaultGraphType(field.Type)

    return (
        <EventData
            field={field}
            selectedActivity={item.ActivityId}
            graphType={resolvedGraphType}
            startDate={startDate}
            endDate={endDate}
            dashboardOptions={hideControls ? null : (
                <DashboardCardOptions
                    item={item}
                    fieldType={field.Type}
                    currentGraphType={resolvedGraphType}
                    subItems={[]}
                />
            )}
        />
    )
}

// Indicator — thin wrapper around IndicatorVisualization. Owns the
// persona-filter state so the standalone analysis path stays chip-less.
const IndicatorCardContent = ({ item, graphType, personas, personasEnabled, startDate, endDate, periodRange, hideControls, onCompareData }) => {
    const [selectedPersonaId, setSelectedPersonaId] = useState('')
    return (
        <IndicatorVisualization
            indicatorId={item.IndicatorId}
            effectId={item.EffectId}
            graphType={graphType}
            personas={personas}
            personasEnabled={personasEnabled}
            selectedPersonaId={selectedPersonaId}
            onPersonaChange={setSelectedPersonaId}
            dashboardItem={item}
            startDate={startDate}
            endDate={endDate}
            periodRange={periodRange}
            hideControls={hideControls}
            onCompareData={onCompareData}
        />
    )
}

// Output indicator (from Outputs collection) — thin wrapper around
// OutputVisualization. Owns the persona-filter state so the standalone
// analysis path (which doesn't have a wrapper) keeps its current behaviour.
const OutputIndicatorCardContent = ({ item, graphType, personas, personasEnabled, startDate, endDate, periodRange, hideControls }) => {
    const [selectedPersonaId, setSelectedPersonaId] = useState('')
    return (
        <OutputVisualization
            outputId={item.OutputId}
            graphType={graphType}
            personas={personas}
            personasEnabled={personasEnabled}
            selectedPersonaId={selectedPersonaId}
            onPersonaChange={setSelectedPersonaId}
            dashboardItem={item}
            startDate={startDate}
            endDate={endDate}
            periodRange={periodRange}
            hideControls={hideControls}
        />
    )
}

// Whole activity card (parent) — reuses the Analysis body component.
// Parent cards don't show a per-viz options bar in this phase — the body
// navigates across many items with different types, so a single bar makes
// less sense. GraphType and ColorOverrides still apply via the body.
//
// The persona-filter state lives here (dashboard wrapper) so the standalone
// analysis page — which renders AnalysisActivityCardBody without these props
// — keeps its current chip-less behaviour.
const ActivityCardContent = ({ item, graphType, personas, personasEnabled, compareActive, rangeA, rangeB }) => {
    const activities = useFirestoreGeneral('Activities', 'ID', item.ActivityId || '')
    const activity = activities && activities.length > 0 ? activities[0] : null

    const [selectedPersonaId, setSelectedPersonaId] = useState('')

    if (!activity) return <p className='new-dashboard-card-empty'>Laden...</p>

    return (
        <AnalysisActivityCardBody
            activity={activity}
            headerAction={<TypeLabelPill type='activity' />}
            graphTypeOverride={graphType}
            personas={personas}
            personasEnabled={personasEnabled}
            selectedPersonaId={selectedPersonaId}
            onPersonaChange={setSelectedPersonaId}
            dashboardItem={item}
            compareActive={compareActive}
            rangeA={rangeA}
            rangeB={rangeB}
        />
    )
}

// Whole effect card (parent) — reuses the Analysis body component.
// Resolves the paired targetgroups so the card can filter responses by targetgroup.
const EffectCardContent = ({ item, graphType, personas, personasEnabled, compareActive, rangeA, rangeB }) => {
    const effects = useFirestoreGeneral('OutputEffects', 'ID', item.EffectId || '')
    const effect = effects && effects.length > 0 ? effects[0] : null

    const {
        targetgroups,
        researchIdsByTargetgroup,
        multipleQuestionnaires,
        loading: targetgroupsLoading,
    } = useEffectTargetgroups(effect)

    const [selectedTargetgroupId, setSelectedTargetgroupId] = useState('')
    const [selectedPersonaId, setSelectedPersonaId] = useState('')

    // If the selected targetgroup disappears (rare, e.g. activity unlinked mid-session),
    // reset the selection so the UI doesn't hold a dangling filter.
    useEffect(() => {
        if (!selectedTargetgroupId) return
        const stillAvailable = targetgroups.some((t) => t.ID === selectedTargetgroupId)
        if (!stillAvailable) setSelectedTargetgroupId('')
    }, [targetgroups, selectedTargetgroupId])

    // Memoise so QuestionData can use it directly in effect deps without
    // eslint-disable or stringified-key workarounds. Must sit above any
    // conditional early-return to keep hook order stable.
    const researchIdsOverride = useMemo(
        () => selectedTargetgroupId
            ? (researchIdsByTargetgroup[selectedTargetgroupId] || EMPTY_RESEARCH_IDS)
            : null,
        [selectedTargetgroupId, researchIdsByTargetgroup]
    )

    if (!effect) return <p className='new-dashboard-card-empty'>Laden...</p>

    const isMsi = effect.Standard === 'true'

    const targetgroupFilter = !targetgroupsLoading && targetgroups.length > 0 ? (
        <DashboardEffectTargetgroupFilter
            targetgroups={targetgroups}
            selectedTargetgroupId={selectedTargetgroupId}
            onChange={setSelectedTargetgroupId}
            multipleQuestionnaires={multipleQuestionnaires}
        />
    ) : null

    return (
        <AnalysisEffectCardBody
            effect={effect}
            headerAction={<TypeLabelPill type='effect' isMsi={isMsi} />}
            graphTypeOverride={graphType}
            targetgroupFilter={targetgroupFilter}
            personas={personas}
            personasEnabled={personasEnabled}
            selectedPersonaId={selectedPersonaId}
            onPersonaChange={setSelectedPersonaId}
            onClearFilters={() => {
                setSelectedTargetgroupId('')
                setSelectedPersonaId('')
            }}
            researchIdsOverride={researchIdsOverride}
            filterByResearchId={Boolean(researchIdsOverride)}
            dashboardItem={item}
            compareActive={compareActive}
            rangeA={rangeA}
            rangeB={rangeB}
        />
    )
}

// Whole research card (parent) — reuses the Analysis body component
const ResearchCardContent = ({ item, graphType, personas, personasEnabled, compareActive, rangeA, rangeB }) => {
    const researches = useFirestoreGeneral('Research', 'ID', item.ResearchId || '')
    const research = researches && researches.length > 0 ? researches[0] : null

    const [selectedPersonaId, setSelectedPersonaId] = useState('')

    if (!research) return <p className='new-dashboard-card-empty'>Laden...</p>

    return (
        <AnalysisResearchCardBody
            research={research}
            headerAction={<TypeLabelPill type='research' />}
            graphTypeOverride={graphType}
            personas={personas}
            personasEnabled={personasEnabled}
            selectedPersonaId={selectedPersonaId}
            onPersonaChange={setSelectedPersonaId}
            compareActive={compareActive}
            rangeA={rangeA}
            rangeB={rangeB}
        />
    )
}

const DashboardVisualisationCard = ({ item, personas, personasEnabled }) => {
    // Geen kleurbron meer op dit niveau. Dashboardkaarten renderen buiten een
    // live rapportage, dus de grafieken halen hun kleuren zelf uit het vaste
    // Deccos-palet (useChartColors). De huisstijl van de organisatie en de
    // ColorOverrides op het DashboardItems-document gelden alleen nog binnen
    // een live rapportage — zie docs/features/chart-color-system.md.
    const graphType = item.GraphType // may be undefined → children compute defaults

    // Compare-two-periods (docs/features/compare-two-periods.md).
    // Reconstruct the control value from the two stored fields; tolerate
    // pre-feature docs that have neither (→ all-time single chart).
    const periodValue = useMemo(() => ({
        period: item.Period || { start: null, end: null },
        compare: item.ComparePeriod || { enabled: false, start: null, end: null },
    }), [item.Period, item.ComparePeriod])

    const { rangeA, rangeB, compareActive } = resolveComparePeriods(periodValue)

    const persistPeriod = (value) => {
        if (!item.docid) return
        db.collection('DashboardItems').doc(item.docid).update({
            [DASHBOARD_FILTER_FIELDS.Period]: value.period || { start: null, end: null },
            [DASHBOARD_FILTER_FIELDS.ComparePeriod]: value.compare || { enabled: false, start: null, end: null },
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        })
    }

    const baseProps = { item, graphType, personas, personasEnabled }

    // Parent cards (activity/effect/research) own a header + navigator inside
    // their body; single-item cards don't. Drives both the render split below
    // and (further down) whether the top-level CardLabel renders.
    const isParentCard = item.Type === 'activity' || item.Type === 'effect' || item.Type === 'research'

    // Single-item cards (indicator / output) have no header or navigator inside
    // their content, so compare is a card-level small-multiples wrap: render
    // the chart-only content once per period.
    const renderSingleItem = (periodProps, hideControls, onCompareData) => {
        const sharedProps = {
            ...baseProps,
            startDate: periodProps.startDate,
            endDate: periodProps.endDate,
            periodRange: periodProps.periodRange,
            hideControls,
        }
        switch (item.Type) {
            case 'indicator':
                return <IndicatorCardContent {...sharedProps} onCompareData={onCompareData} />
            case 'output':
                return <OutputCardContent {...sharedProps} />
            case 'outputIndicator':
                return <OutputIndicatorCardContent {...sharedProps} />
            default:
                return <p className='new-dashboard-card-empty'>Onbekend type</p>
        }
    }

    // Parent cards (effect / activity / research) own a header + item navigator,
    // so the body itself handles compare (header once, one shared navigator).
    const renderParent = () => {
        const compareProps = { ...baseProps, compareActive, rangeA, rangeB }
        switch (item.Type) {
            case 'activity':
                return <ActivityCardContent {...compareProps} />
            case 'effect':
                return <EffectCardContent {...compareProps} />
            case 'research':
                return <ResearchCardContent {...compareProps} />
            default:
                return <p className='new-dashboard-card-empty'>Onbekend type</p>
        }
    }

    const renderBody = () => {
        if (isParentCard) return renderParent()
        // Single-item: single period renders as today.
        if (!compareActive) {
            return renderSingleItem({ ...periodRangeToDates(rangeA), periodRange: rangeA }, false)
        }
        // Indicator compare: harvest each period's KPI value and upgrade a
        // single-moment number field to the "A vs B + Δ" view; otherwise the
        // frame renders small multiples. Other single-item types (output) always
        // use small multiples.
        if (item.Type === 'indicator') {
            return (
                <IndicatorCompareView
                    rangeA={rangeA}
                    rangeB={rangeB}
                    renderViz={(range, side, onCompareData) =>
                        renderSingleItem({ ...periodRangeToDates(range), periodRange: range }, true, onCompareData)}
                />
            )
        }
        return (
            <ComparisonFrame
                rangeA={rangeA}
                rangeB={rangeB}
                renderViz={(range) => renderSingleItem({ ...periodRangeToDates(range), periodRange: range }, true)}
            />
        )
    }

    // (isParentCard declared above — it also drives the render split.)

    // Dashboard analytics cards show the empty-state CTA. Carries the card type
    // down to the shared chart layer so the "no data yet" copy/CTA matches.
    // sankeyAvailable is published by MatrixData and read by the graph-type picker.
    const [sankeyAvailable, setSankeyAvailable] = useState(false)
    const emptyStateContext = useMemo(
        () => ({ type: item.Type, showCta: true, sankeyAvailable, setSankeyAvailable }),
        [item.Type, sankeyAvailable]
    )

    // Op groot scherm (idee #86-reeks, inbox #91): dezelfde content nogmaals in
    // een schermvullende modal — de grafieken (ResponsiveContainer, width 100%)
    // groeien mee met de ruimte. Het icoon heeft een eigen vak in de onderbalk:
    // rechts van de andere iconen, links van het verwijder-icoon.
    const [fullscreenOpen, setFullscreenOpen] = useState(false)
    const expandSlot = useContext(CardHeaderSlotContext)?.expandSlot
    const fullscreenTrigger = (
        <span className='dashboard-card-fullscreen-slot'>
            <Tooltip content='Op groot scherm' top='-40px'>
                <button
                    type='button'
                    className='dashboard-card-options-icon'
                    aria-label='Op groot scherm'
                    onClick={() => setFullscreenOpen(true)}
                >
                    <OpenInFullOutlinedIcon />
                </button>
            </Tooltip>
        </span>
    )

    return (
        <EmptyStateProvider value={emptyStateContext}>
            <div className='new-dashboard-visualisation-card-inner'>
                {/* Renders nothing inline — its icon portals into the card's
                    bottom bar (DashboardCardFooterContext), the Modal to #root. */}
                <CardPeriodControl value={periodValue} onChange={persistPeriod} />
                {expandSlot ? createPortal(fullscreenTrigger, expandSlot) : fullscreenTrigger}
                {!isParentCard && <CardLabel item={item} />}
                {renderBody()}
            </div>
            {fullscreenOpen && (
                <Modal
                    modalOpen={fullscreenOpen}
                    setModalOpen={setFullscreenOpen}
                    pageHeader={item.Title || 'Grafiek'}
                    actions={null}
                    content={
                        <div className='dashboard-card-fullscreen-content'>
                            {renderBody()}
                        </div>
                    }
                />
            )}
        </EmptyStateProvider>
    )
}

export default DashboardVisualisationCard
